import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import { Edit as EditIcon, Delete as DeleteIcon, Plus, Sparkles as HrSourcesLogoIcon } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

const StandardBadge = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 36,
  height: 36,
  padding: '0 8px',
  borderRadius: 6,
  backgroundColor: 'rgba(255, 171, 0, 0.16)',
  color: 'rgb(183, 110, 0)',
  fontSize: '0.85rem',
  fontWeight: 700
}));

export default function HrFonti() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [nome, setNome] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [attivo, setAttivo] = useState(true);

  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('nome');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await api.get('/hr/fonti');
      setItems(res.data || []);
    } catch (error) {
      console.error('Error fetching fonti:', error);
      toast.error('Errore nel caricamento delle fonti');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sortedItems = [...items].sort((a, b) => {
    const valueA = a[orderBy];
    const valueB = b[orderBy];
    if (valueA === null || valueA === undefined) return 1;
    if (valueB === null || valueB === undefined) return -1;
    if (typeof valueA === 'number' && typeof valueB === 'number') {
      return order === 'asc' ? valueA - valueB : valueB - valueA;
    }
    const strA = String(valueA).toLowerCase();
    const strB = String(valueB).toLowerCase();
    if (strA < strB) return order === 'asc' ? -1 : 1;
    if (strA > strB) return order === 'asc' ? 1 : -1;
    return 0;
  });

  useEffect(() => {
    fetchItems();
  }, []);

  const openCreateDialog = () => {
    setEditingItem(null);
    setNome('');
    setDescrizione('');
    setAttivo(true);
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setNome(item.nome || '');
    setDescrizione(item.descrizione || '');
    setAttivo(Boolean(item.attivo));
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!nome.trim()) {
      toast.error('Il nome è obbligatorio');
      return;
    }
    try {
      setSaving(true);
      if (editingItem) {
        await api.put(`/hr/fonti/${editingItem.id}`, {
          nome: nome.trim(),
          descrizione: descrizione || null,
          attivo
        });
        toast.success('Fonte aggiornata');
      } else {
        await api.post('/hr/fonti', {
          nome: nome.trim(),
          descrizione: descrizione || null,
          attivo
        });
        toast.success('Fonte creata');
      }
      setDialogOpen(false);
      await fetchItems();
    } catch (error) {
      console.error('Error saving fonte:', error);
      const message =
        error.response?.data?.message || 'Errore nel salvataggio della fonte';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (item) => {
    setDeleteTarget(item || null);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    if (Number(deleteTarget?.total_candidati || 0) > 0) {
      toast.info('Elemento in uso: disattiva invece di eliminare');
      return;
    }
    try {
      setDeleteLoading(true);
      await api.delete(`/hr/fonti/${deleteTarget.id}`);
      toast.success('Fonte eliminata');
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      await fetchItems();
    } catch (error) {
      console.error('Error deleting fonte:', error);
      const message =
        error.response?.data?.message || 'Errore nell\'eliminazione della fonte';
      toast.error(message);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <HrSourcesLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              Fonti
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Canali di provenienza (job board, referral, campagne, ecc.)
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Plus size={18} />}
          onClick={openCreateDialog}
        >
          Nuova fonte
        </Button>
      </Box>

      <StyledCard>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sortDirection={orderBy === 'nome' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'nome'}
                    direction={orderBy === 'nome' ? order : 'asc'}
                    onClick={() => handleRequestSort('nome')}
                  >
                    Nome
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'descrizione' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'descrizione'}
                    direction={orderBy === 'descrizione' ? order : 'asc'}
                    onClick={() => handleRequestSort('descrizione')}
                  >
                    Descrizione
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="center"
                  sortDirection={orderBy === 'attivo' ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === 'attivo'}
                    direction={orderBy === 'attivo' ? order : 'asc'}
                    onClick={() => handleRequestSort('attivo')}
                  >
                    Attivo
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="center"
                  sortDirection={orderBy === 'total_candidati' ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === 'total_candidati'}
                    direction={orderBy === 'total_candidati' ? order : 'asc'}
                    onClick={() => handleRequestSort('total_candidati')}
                  >
                    Totale Candidati
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">% sul totale</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      Nessuna fonte presente
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedItems
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{item.nome}</TableCell>
                    <TableCell>{item.descrizione}</TableCell>
                    <TableCell align="center">
                      <Switch checked={Boolean(item.attivo)} disabled />
                    </TableCell>
                    <TableCell align="center">
                      <StandardBadge>
                        {item.total_candidati || 0}
                      </StandardBadge>
                    </TableCell>
                    <TableCell align="center">
                      {parseFloat(item.percentage || 0) > 0 ? (
                        <div style={{ position: 'relative', display: 'inline-flex' }}>
                          <CircularProgress
                            variant="determinate"
                            value={100}
                            size={36}
                            thickness={6}
                            sx={{ color: 'rgba(145, 158, 171, 0.24)' }}
                          />
                          <CircularProgress
                            variant="determinate"
                            value={parseFloat(item.percentage || 0)}
                            size={36}
                            thickness={6}
                            sx={{
                              position: 'absolute',
                              left: 0,
                              color: 'rgb(255, 171, 0)'
                            }}
                          />
                          <Box
                            sx={{
                              top: 0,
                              left: 0,
                              bottom: 0,
                              right: 0,
                              position: 'absolute',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 'bold', fontSize: '0.65rem' }}
                            >
                              {parseFloat(item.percentage || 0).toFixed(0)}%
                            </Typography>
                          </Box>
                        </div>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => openEditDialog(item)}
                        sx={{ mr: 1 }}
                      >
                        <EditIcon size={18} />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(item)}
                      >
                        <DeleteIcon size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={items.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (deleteLoading) return;
          setDeleteDialogOpen(false);
          setDeleteTarget(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: 'rgba(239, 68, 68, 0.10)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <DeleteIcon size={20} color="#ef4444" />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Elimina fonte
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Questa operazione non può essere annullata
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Nome
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                {deleteTarget?.nome || '-'}
              </Typography>
              {deleteTarget?.descrizione ? (
                <Typography variant="body2" color="text.secondary">
                  {deleteTarget.descrizione}
                </Typography>
              ) : null}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Stato
                </Typography>
                <Chip
                  label={deleteTarget?.attivo ? 'Attivo' : 'Non attivo'}
                  size="small"
                  color={deleteTarget?.attivo ? 'success' : 'default'}
                  variant="outlined"
                  sx={{ borderRadius: '10px' }}
                />
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Candidati associati
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {deleteTarget?.total_candidati || 0}
                  {Number(deleteTarget?.total_candidati || 0) > 0 ? ' (attenzione: in uso)' : ''}
                </Typography>
                {Number(deleteTarget?.total_candidati || 0) > 0 ? (
                  <Typography variant="caption" sx={{ color: 'rgb(183, 110, 0)' }}>
                    Non eliminabile: disattiva questa fonte per mantenerla nello storico.
                  </Typography>
                ) : null}
                {deleteTarget?.percentage ? (
                  <Typography variant="caption" color="text.secondary">
                    {parseFloat(deleteTarget.percentage || 0).toFixed(0)}% sul totale
                  </Typography>
                ) : null}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeleteTarget(null);
            }}
            disabled={deleteLoading}
          >
            Annulla
          </Button>
          <Tooltip
            title={
              Number(deleteTarget?.total_candidati || 0) > 0
                ? 'Elemento in uso: disattiva invece di eliminare'
                : ''
            }
          >
            <span>
              <Button
                onClick={handleDeleteConfirm}
                color="error"
                variant="contained"
                disabled={deleteLoading || Number(deleteTarget?.total_candidati || 0) > 0}
                sx={{ borderRadius: '12px' }}
              >
                {deleteLoading ? 'Eliminazione...' : 'Elimina'}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogOpen}
        onClose={() => (saving ? null : setDialogOpen(false))}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingItem ? 'Modifica fonte' : 'Nuova fonte'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Descrizione"
              value={descrizione}
              onChange={(e) => setDescrizione(e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={attivo}
                  onChange={(e) => setAttivo(e.target.checked)}
                  color="primary"
                />
              }
              label="Attivo"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Annulla
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={saving}
          >
            {saving ? 'Salvataggio...' : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
